/**
 * MMTG binary decoder. Zero-copy: vertex buffers are `Float32Array` views
 * into the source `ArrayBuffer`, not detached copies. This matters for
 * 2–7 MB files where a copy would double transient memory pressure
 * on cold start.
 *
 * Spec: MMTG_FORMAT.md. All integers little-endian.
 *
 * The reader does not check the 8-byte signature itself — it returns it
 * (`MmtgFile.signature`). A downloaded map bundle is checked against its
 * schema before it is used (`mapDelivery/bundleVerifier`); the bundled set
 * is checked by its tests.
 */

import {
  BYTES_PER_VERTEX,
  CURRENT_VERSIONS,
  HEADER_SIZE,
  MMTG_MAGIC,
  MmtgElementType,
} from './format'

export interface MmtgEntry {
  readonly id: string
  readonly renderFloor: number
  readonly flags: number
  readonly fillVertices: Float32Array
  readonly strokeVertices: Float32Array
}

export interface MmtgFile {
  readonly elementType: MmtgElementType
  readonly version: number
  /** Header bytes 12..20 as written: the little-endian u64 signature. */
  readonly signature: bigint
  readonly entries: readonly MmtgEntry[]
}

export function readMmtg(buf: ArrayBuffer, expectedType: MmtgElementType): MmtgFile {
  if (buf.byteLength < HEADER_SIZE) {
    throw new Error(`MMTG: file too small (${buf.byteLength} bytes)`)
  }
  const view = new DataView(buf)
  const bytes = new Uint8Array(buf)

  // Magic "MMTG".
  for (let i = 0; i < 4; i++) {
    if (bytes[i] !== MMTG_MAGIC[i]) {
      throw new Error(
        `MMTG: bad magic at byte ${i}: got 0x${bytes[i].toString(16)}, want 0x${MMTG_MAGIC[i].toString(16)}`,
      )
    }
  }

  const elementType = view.getUint32(4, true) as MmtgElementType
  if (elementType !== expectedType) {
    throw new Error(`MMTG: elementType ${elementType} does not match expected ${expectedType}`)
  }

  const version = view.getUint32(8, true)
  const currentVersion = CURRENT_VERSIONS[elementType]
  if (currentVersion === undefined) {
    throw new Error(`MMTG: unknown elementType ${elementType}`)
  }
  if (version !== currentVersion) {
    throw new Error(
      `MMTG: version mismatch for elementType ${elementType}: file=${version}, reader=${currentVersion}`,
    )
  }

  const signature = view.getBigUint64(12, true)
  const count = view.getUint32(20, true)

  const entries: MmtgEntry[] = []
  const decoder = new TextDecoder('utf-8', { fatal: true })
  let offset = HEADER_SIZE

  for (let i = 0; i < count; i++) {
    if (offset + 4 > buf.byteLength) {
      throw new Error(`MMTG: truncated entry ${i} header`)
    }
    const idLen = view.getUint32(offset, true)
    offset += 4
    if (idLen > 1024) {
      throw new Error(`MMTG: entry ${i} idLen ${idLen} exceeds MAX_ID_BYTES=1024`)
    }
    if (offset + idLen + 16 > buf.byteLength) {
      throw new Error(`MMTG: truncated entry ${i} body`)
    }
    const id = decoder.decode(new Uint8Array(buf, offset, idLen))
    offset += idLen
    const renderFloor = view.getUint32(offset, true)
    offset += 4
    const flags = view.getUint32(offset, true)
    offset += 4
    const fillCount = view.getUint32(offset, true)
    offset += 4
    const strokeCount = view.getUint32(offset, true)
    offset += 4

    const fillBytes = fillCount * BYTES_PER_VERTEX
    const strokeBytes = strokeCount * BYTES_PER_VERTEX
    if (offset + fillBytes + strokeBytes > buf.byteLength) {
      throw new Error(
        `MMTG: truncated vertex payload for entry ${i} (${id}): need ${fillBytes + strokeBytes}, have ${buf.byteLength - offset}`,
      )
    }

    // Zero-copy views. `Float32Array` requires 4-byte alignment of the
    // offset into the source buffer — satisfied because every preceding
    // field is 4-byte-aligned except `id`, which is padded implicitly
    // by the UInt32s surrounding it: idLen(4) + id(idLen) + renderFloor(4)
    // + flags(4) + fillCount(4) + strokeCount(4) = 20 + idLen. Float32
    // alignment therefore requires idLen % 4 === 0. The generator doesn't
    // enforce this, so fall back to a copy when the offset is misaligned.
    const fillVertices = sliceFloat32(buf, offset, fillCount * 6)
    offset += fillBytes
    const strokeVertices = sliceFloat32(buf, offset, strokeCount * 6)
    offset += strokeBytes

    entries.push({ id, renderFloor, flags, fillVertices, strokeVertices })
  }

  return { elementType, version, signature, entries }
}

function sliceFloat32(buf: ArrayBuffer, byteOffset: number, length: number): Float32Array {
  if (length === 0) return new Float32Array(0)
  if (byteOffset % 4 === 0) {
    return new Float32Array(buf, byteOffset, length)
  }
  // Misaligned — copy into a fresh buffer.
  const copy = new Uint8Array(length * 4)
  copy.set(new Uint8Array(buf, byteOffset, length * 4))
  return new Float32Array(copy.buffer)
}
